import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";

/**
 * Registration form.
 *
 * Policy versions are ALWAYS fetched from the server at mount time and
 * used verbatim in the POST payload — never hardcoded (fix cycle
 * 2026-09-24: same-bug-class cleanup after Mark's launch-day
 * consent-loop bug). The Register button stays disabled until the
 * fetch resolves so a slow network can never let a stale version
 * slip out; if the fetch errors, we honestly surface it rather than
 * fall back to a hardcoded constant that could silently drift.
 */
export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Purchase-intent handoff — Landing's paid CTA sends signed-out
  // buyers here as `/register?intent_slug=three_directions`. After
  // successful registration we redirect back to `/plans?intent_slug=…`
  // so Plans.jsx can auto-invoke checkout for them (single click flow).
  const searchParams = new URLSearchParams(location.search);
  const intentSlug = searchParams.get("intent_slug");
  const [form, setForm] = useState({
    email: "",
    password: "",
    display_name: "",
    consent_terms: false,
    consent_privacy: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [policyVersions, setPolicyVersions] = useState(null); // null = still loading

  useEffect(() => {
    (async () => {
      try {
        const [t, p] = await Promise.all([
          api.get("/policies/terms"),
          api.get("/policies/privacy"),
        ]);
        setPolicyVersions({ terms: t.data.version, privacy: p.data.version });
      } catch (err) {
        setError(extractApiError(err,
          "We couldn't load the current Terms and Privacy versions. Please refresh the page."));
      }
    })();
  }, []);

  const versionsReady = policyVersions?.terms && policyVersions?.privacy;
  const canSubmit =
    versionsReady &&
    form.email.trim() &&
    form.password.length >= 8 &&
    form.consent_terms &&
    form.consent_privacy &&
    !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!versionsReady) {
      setError("We're still loading the current policy versions — please wait a moment and retry.");
      return;
    }
    setSubmitting(true);
    try {
      await register({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        display_name: form.display_name.trim() || null,
        consent_terms: form.consent_terms,
        consent_privacy: form.consent_privacy,
        terms_version: policyVersions.terms,
        privacy_version: policyVersions.privacy,
      });
      // Intent-preserving redirect: paid CTAs from Landing send us
      // here with ?intent_slug=three_directions (or free_explorer);
      // route back to /plans which will auto-invoke checkout for the
      // paid slug. Free slug still goes to plans so users see both
      // options one more time before starting.
      if (intentSlug) {
        const qs = new URLSearchParams({ intent_slug: intentSlug });
        navigate(`/plans?${qs.toString()}`, { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      setError(extractApiError(err, "Registration failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-base text-fg-primary flex flex-col">
      <a href="#main-content" className="cra3d-skip-link" data-testid="skip-to-main">
        Skip to main content
      </a>
      <header className="border-b border-border-subtle bg-bg-surface">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Wordmark size="sm" testid="register-header-wordmark" /></Link>
          <Link to="/login" data-testid="register-to-login-link" className="text-sm text-fg-secondary hover:text-fg-primary transition-colors duration-200">Already have an account?</Link>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="flex-1 flex items-start sm:items-center justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-md">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary">Create your account</h1>
          <p className="mt-2 text-sm text-fg-secondary">
            No credit card, no marketing emails. This is a private development build.
          </p>

          {error && (
            <Alert variant="destructive" className="mt-6" data-testid="register-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                data-testid="register-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-bg-surface border-border-subtle text-fg-primary"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="display_name">Display name <span className="text-fg-secondary text-xs">(optional)</span></Label>
              <Input
                id="display_name"
                type="text"
                autoComplete="nickname"
                data-testid="register-display-name-input"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                className="bg-bg-surface border-border-subtle text-fg-primary"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                data-testid="register-password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="bg-bg-surface border-border-subtle text-fg-primary"
              />
              <p className="text-xs text-fg-secondary">Minimum 8 characters. Must contain a letter and a number.</p>
            </div>

            <div className="space-y-3 rounded-md border border-border-subtle bg-bg-surface p-4">
              <label className="flex gap-3 items-start">
                <Checkbox
                  id="consent_terms"
                  data-testid="register-consent-terms"
                  checked={form.consent_terms}
                  onCheckedChange={(v) => setForm({ ...form, consent_terms: !!v })}
                  aria-label="I have read and agree to the Terms of Use"
                  className="mt-0.5"
                />
                <span className="text-sm text-fg-secondary leading-relaxed">
                  I have read and agree to the{" "}
                  <Link to="/terms" data-testid="register-terms-link" className="text-accent-warm underline underline-offset-2 decoration-accent-warm/40 hover:decoration-accent-warm">
                    Terms of Use{policyVersions?.terms ? ` (v${policyVersions.terms})` : ""}
                  </Link>
                  .
                </span>
              </label>
              <label className="flex gap-3 items-start">
                <Checkbox
                  id="consent_privacy"
                  data-testid="register-consent-privacy"
                  checked={form.consent_privacy}
                  onCheckedChange={(v) => setForm({ ...form, consent_privacy: !!v })}
                  aria-label="I have read and understand the Privacy Notice"
                  className="mt-0.5"
                />
                <span className="text-sm text-fg-secondary leading-relaxed">
                  I have read and understand the{" "}
                  <Link to="/privacy" data-testid="register-privacy-link" className="text-accent-warm underline underline-offset-2 decoration-accent-warm/40 hover:decoration-accent-warm">
                    Privacy Notice{policyVersions?.privacy ? ` (v${policyVersions.privacy})` : ""}
                  </Link>
                  .
                </span>
              </label>
            </div>

            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="register-submit-btn"
              className="w-full bg-accent-warm text-bg-base hover:bg-accent-warm-hover disabled:opacity-50"
            >
              {submitting ? "Creating account…" : "Create account"}
            </Button>
            <p className="text-xs text-fg-secondary text-center">
              Have an account?{" "}
              <Link to="/login" data-testid="register-login-link" className="text-accent-warm underline underline-offset-2 decoration-accent-warm/40 hover:decoration-accent-warm">
                Log in
              </Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
